export const TagDeviceIds = {
  Master: 0,
  Aggregated: 32_767,
} as const;

export const TagNameDelimiter = ":";

export function getTagLogicalName(tagNameOrPrefix: string): string {
  return tagNameOrPrefix.endsWith(TagNameDelimiter)
    ? tagNameOrPrefix.slice(0, -TagNameDelimiter.length)
    : tagNameOrPrefix;
}

export function withTagDeviceId(tagName: string, deviceId: number | string): string {
  const logicalTagName = getTagLogicalName(tagName).replace(/:\d+$/, "");
  return `${logicalTagName}${TagNameDelimiter}${deviceId}`;
}

export function isTagForDevice(logicalTagName: string, tagName: string): boolean {
  return tagName.startsWith(withTagDeviceId(logicalTagName, ""));
}

export type TagGroupDefinition = {
  prefix: string;
  tags?: readonly string[];
  groups?: Record<string, TagGroupDefinition>;
};

export type CompiledTag = {
  logicalName: string;
  aggregated: string;
  master: string;
  device: (deviceId: number | string) => string;
};

type CompiledGroup<Group extends TagGroupDefinition> = (Group extends {
  tags: infer Names extends readonly string[];
}
  ? { [Name in Names[number]]: CompiledTag }
  : object) &
  (Group extends { groups: infer Groups extends Record<string, TagGroupDefinition> }
    ? { [Name in keyof Groups]: CompiledGroup<Groups[Name]> }
    : object);

function compileTagGroup<Group extends TagGroupDefinition>(
  group: Group,
  parentPrefix?: string
): CompiledGroup<Group> {
  const prefix = parentPrefix ? `${parentPrefix}:${group.prefix}` : group.prefix;
  const compiledTags = Object.fromEntries(
    (group.tags ?? []).map((name) => {
      const logicalName = `${prefix}:${name}`;
      return [
        name,
        {
          logicalName,
          aggregated: `${logicalName}:${TagDeviceIds.Aggregated}`,
          master: `${logicalName}:${TagDeviceIds.Master}`,
          device: (deviceId: number | string) => `${logicalName}:${deviceId}`,
        },
      ];
    })
  );
  const compiledGroups = Object.fromEntries(
    Object.entries(group.groups ?? {}).map(([name, childGroup]) => [
      name,
      compileTagGroup(childGroup, prefix),
    ])
  );
  return { ...compiledTags, ...compiledGroups } as CompiledGroup<Group>;
}

export function compileTagCatalog<Catalog extends Record<string, TagGroupDefinition>>(
  catalog: Catalog
): { [Name in keyof Catalog]: CompiledGroup<Catalog[Name]> } {
  return Object.fromEntries(
    Object.entries(catalog).map(([name, group]) => [name, compileTagGroup(group)])
  ) as { [Name in keyof Catalog]: CompiledGroup<Catalog[Name]> };
}
